// Step 3: Epic Integration

import { useState } from 'react';
import { Check, AlertCircle, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import type { WizardStepProps } from '@/lib/onboarding/types';
import { EPIC_PUSHBACK_DESTINATIONS } from '@/lib/onboarding/types';
import { useClientOnboarding } from '@/hooks/useClientOnboarding';

const MAPPING_CHECKS = [
  { id: 'patient', label: 'Patient Resource', description: 'Demographics, MRN, DOB' },
  { id: 'coverage', label: 'Coverage Resource', description: 'Insurance, Member ID, Group' },
  { id: 'encounter', label: 'Encounter Resource', description: 'Visit context, providers' },
];

export function Step3EpicIntegration({ clientId, progress, onComplete, onBack }: WizardStepProps) {
  const { completeStep, isCompleting } = useClientOnboarding(clientId);
  
  const [epicEnvironment, setEpicEnvironment] = useState<'none' | 'sandbox' | 'production'>(
    progress.epic_environment || 'none'
  );
  const [smartClientId, setSmartClientId] = useState(progress.epic_smart_client_id || '');
  const [pushbackEnabled, setPushbackEnabled] = useState(progress.epic_pushback_enabled);
  const [pushbackDestinations, setPushbackDestinations] = useState<string[]>(
    progress.epic_pushback_destinations || []
  );
  const [mappingValidated, setMappingValidated] = useState(progress.epic_mapping_validated);
  const [mappingChecks, setMappingChecks] = useState<Record<string, boolean>>({
    patient: false,
    coverage: false,
    encounter: false,
  });

  const togglePushbackDestination = (dest: string) => {
    setPushbackDestinations(prev => 
      prev.includes(dest) 
        ? prev.filter(d => d !== dest)
        : [...prev, dest]
    );
  };

  const allMappingsChecked = Object.values(mappingChecks).every(Boolean);

  const handleValidateMapping = () => {
    // In production, this would call an API to validate Epic mapping
    if (allMappingsChecked) {
      setMappingValidated(true);
    }
  };

  const handleSubmit = () => {
    completeStep({
      step: 3,
      stepData: {
        epic_environment: epicEnvironment,
        epic_smart_client_id: smartClientId || null,
        epic_pushback_enabled: pushbackEnabled,
        epic_pushback_destinations: pushbackDestinations,
        epic_mapping_validated: mappingValidated,
      },
    }, {
      onSuccess: onComplete,
    });
  };

  const isValid = epicEnvironment === 'none' || 
    (smartClientId && (!pushbackEnabled || pushbackDestinations.length > 0));

  return (
    <Card>
      <CardHeader>
        <CardTitle>Epic Integration</CardTitle>
        <CardDescription>
          Configure Epic EHR connectivity for bi-directional data flow. This step is optional.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Epic Environment */}
        <div className="space-y-3">
          <Label className="text-base font-medium">Epic Environment</Label>
          <RadioGroup
            value={epicEnvironment}
            onValueChange={(v) => setEpicEnvironment(v as 'none' | 'sandbox' | 'production')}
            className="grid gap-3"
          >
            <div className="flex items-center space-x-3 p-3 border rounded-lg">
              <RadioGroupItem value="none" id="none" />
              <Label htmlFor="none" className="flex-1 cursor-pointer">
                <span className="font-medium">None</span>
                <span className="block text-sm text-muted-foreground">
                  No Epic integration at this time
                </span>
              </Label>
            </div>
            <div className="flex items-center space-x-3 p-3 border rounded-lg">
              <RadioGroupItem value="sandbox" id="sandbox" />
              <Label htmlFor="sandbox" className="flex-1 cursor-pointer">
                <span className="font-medium">Sandbox</span>
                <Badge variant="outline" className="ml-2">Testing</Badge>
                <span className="block text-sm text-muted-foreground">
                  Connect to Epic sandbox for testing
                </span>
              </Label>
            </div>
            <div className="flex items-center space-x-3 p-3 border rounded-lg">
              <RadioGroupItem value="production" id="production" />
              <Label htmlFor="production" className="flex-1 cursor-pointer">
                <span className="font-medium">Production</span>
                <Badge variant="default" className="ml-2">Live</Badge>
                <span className="block text-sm text-muted-foreground">
                  Connect to live Epic instance
                </span>
              </Label>
            </div>
          </RadioGroup>
        </div>

        {epicEnvironment !== 'none' && (
          <>
            {/* SMART Client ID */}
            <div className="space-y-2">
              <Label htmlFor="smart-client-id">SMART Client ID</Label>
              <Input
                id="smart-client-id"
                placeholder="Enter your Epic SMART on FHIR client ID"
                value={smartClientId}
                onChange={(e) => setSmartClientId(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Provided by Epic after app registration
              </p>
            </div>

            {/* Epic Pushback */}
            <div className="space-y-4 border-t pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-base font-medium">Enable Epic Pushback</Label>
                  <p className="text-sm text-muted-foreground">
                    Send authorization status updates back to Epic
                  </p>
                </div>
                <Switch
                  checked={pushbackEnabled}
                  onCheckedChange={setPushbackEnabled}
                />
              </div>

              {pushbackEnabled && (
                <div className="ml-6 space-y-3">
                  <Label>Pushback Destinations</Label>
                  <div className="space-y-2">
                    {EPIC_PUSHBACK_DESTINATIONS.map(dest => (
                      <div key={dest.id} className="flex items-center space-x-2">
                        <Checkbox
                          id={dest.id}
                          checked={pushbackDestinations.includes(dest.id)}
                          onCheckedChange={() => togglePushbackDestination(dest.id)}
                        />
                        <Label htmlFor={dest.id} className="cursor-pointer">
                          {dest.label}
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Mapping Validation */}
            <div className="space-y-4 border-t pt-6">
              <div>
                <Label className="text-base font-medium">FHIR Resource Mapping</Label>
                <p className="text-sm text-muted-foreground">
                  Confirm that required FHIR resources are properly mapped
                </p>
              </div>

              <div className="space-y-3">
                {MAPPING_CHECKS.map(check => (
                  <div 
                    key={check.id}
                    className="flex items-start space-x-3 p-3 border rounded-lg"
                  >
                    <Checkbox
                      id={check.id}
                      checked={mappingChecks[check.id]}
                      onCheckedChange={(checked) => 
                        setMappingChecks(prev => ({ ...prev, [check.id]: !!checked }))
                      }
                    />
                    <div>
                      <Label htmlFor={check.id} className="cursor-pointer font-medium">
                        {check.label}
                      </Label>
                      <p className="text-sm text-muted-foreground">{check.description}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  onClick={handleValidateMapping}
                  disabled={!allMappingsChecked || mappingValidated}
                >
                  {mappingValidated ? (
                    <>
                      <Check className="mr-2 h-4 w-4 text-green-500" />
                      Validated
                    </>
                  ) : (
                    'Validate Mapping'
                  )}
                </Button>
                {mappingValidated && (
                  <Badge variant="outline" className="text-green-600 border-green-600">
                    Ready for Go-Live
                  </Badge>
                )}
              </div>
            </div>
          </>
        )}

        {epicEnvironment === 'none' && (
          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>
              You can configure Epic integration later from Client Settings. 
              The system will operate without EHR connectivity.
            </AlertDescription>
          </Alert>
        )}

        {/* Navigation */}
        <div className="flex justify-between pt-4">
          <Button variant="outline" onClick={onBack}>
            Back
          </Button>
          <Button 
            onClick={handleSubmit} 
            disabled={!isValid || isCompleting}
          >
            {isCompleting ? 'Saving...' : 'Continue to Ruleset Templates'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
